import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  useCreateBankMutation,
  useUpdateBankMutation,
} from "../../../store/api/admin/bankSlice";
import { useGetAllUsersQuery } from "../../../store/api/userSlice";
import { getErrorMessage } from "../../../utils";
import { useParams } from "react-router-dom";

const initialState = {
  user_type: "",
  user_id: "",
  omni_api_key: "",
  merchant_id: "",
  payment_method_id: "",
};

const AddBankDetails = ({ show, close, editData, role }) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const isEdit = Boolean(editData);

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Load users for the select box (role-based)
  const { data: usersData, isLoading: isUsersLoading } = useGetAllUsersQuery(
    { role },   // no page/limit → backend returns all users
    { skip: !show },
  );

  const users = usersData?.data?.users ?? [];

  // Pre-fill form when editing
  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        user_id: editData.user_id ?? "",
        user_type: role,
        omni_api_key: "",
        merchant_id: "",
        payment_method_id: "",
      });
    }
    setErrors({});
  }, [editData, isEdit, show]);

  const [createBank, { isLoading: isCreating }] = useCreateBankMutation();
  const [updateBank, { isLoading: isUpdating }] = useUpdateBankMutation();

  const isLoading = isCreating || isUpdating;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: value ? "" : prev[name] }));
  };

  const handleUserSelect = (option) => {
    setFormData((prev) => ({ ...prev, user_id: option ? option.value : "" }));
    setErrors((prev) => ({ ...prev, user_id: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!isEdit && !formData.user_id) newErrors.user_id = "Please select user";
    if (!isEdit && !formData.omni_api_key.trim())
      newErrors.omni_api_key = "Omni API Key is required";
    if (!isEdit && !formData.merchant_id.trim())
      newErrors.merchant_id = "Merchant ID is required";
    if (!isEdit && !formData.payment_method_id.trim())
      newErrors.payment_method_id = "Payment Method ID is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    try {
      if (isEdit) {
        const payload = { user_type: formData.user_type };
        if (formData.omni_api_key.trim())
          payload.omni_api_key = formData.omni_api_key.trim();
        if (formData.merchant_id.trim())
          payload.merchant_id = formData.merchant_id.trim();
        if (formData.payment_method_id.trim())
          payload.payment_method_id = formData.payment_method_id.trim();

        const res = await updateBank({
          id: editData.id,
          data: payload,
        }).unwrap();
        toast.success(res?.message || t("updatedSuccessfully"));
      } else {
        const payload = {
          user_id: Number(formData.user_id),
          user_type: formData.user_type,
          omni_api_key: formData.omni_api_key.trim(),
          merchant_id: formData.merchant_id.trim(),
          payment_method_id: formData.payment_method_id.trim(),
        };
        const res = await createBank(payload).unwrap();
        toast.success(res?.message || t("createdSuccessfully"));
      }
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleClose = () => {
    setFormData(initialState);
    setErrors({});
    close();
  };
  useEffect(() => {
    if (role) {
      setFormData((prev) => ({ ...prev, user_type: role }));
    }
  }, [role]);

  return (
    <Modal show={show} onHide={handleClose} centered className="VehicleModal">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? `${t("edit")} ${t("bank")} ${t("details")}`
            : `${t("add")} ${t("bank")} ${t("details")}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <ul className="form p-0 m-0 d-flex flex-wrap">
            {/* User Select — only on create */}
            {!isEdit && (
              <li className="col-12">
                <label className="filedHd">
                  {t("user")} <span className="text-danger">*</span>
                </label>
                <Select
                  options={users.map((u) => ({
                    value: u.id,
                    label: `${u.full_name} — ${u.email}`,
                  }))}
                  value={
                    formData.user_id
                      ? (users
                          .filter((u) => u.id === Number(formData.user_id))
                          .map((u) => ({
                            value: u.id,
                            label: `${u.full_name} — ${u.email}`,
                          }))[0] ?? null)
                      : null
                  }
                  onChange={handleUserSelect}
                  isLoading={isUsersLoading}
                  isDisabled={isUsersLoading}
                  isClearable
                  isSearchable
                  placeholder={
                    isUsersLoading
                      ? `${t("loading")}...`
                      : `${t("select")} ${t("user")}`
                  }
                  classNamePrefix="rs"
                />
                {errors?.user_id && (
                  <span className="text-danger small">{errors.user_id}</span>
                )}
              </li>
            )}

            {/* Omni API Key */}
            <li className="col-12">
              <label className="filedHd">
                {t("omni_api_key")}
                {!isEdit && <span className="text-danger"> *</span>}
              </label>
              <input
                type="text"
                name="omni_api_key"
                className="formInput"
                value={formData.omni_api_key}
                onChange={handleChange}
                placeholder={
                  isEdit ? "Leave blank to keep current" : t("omni_api_key")
                }
              />
              {errors?.omni_api_key && (
                <span className="text-danger small">{errors.omni_api_key}</span>
              )}
            </li>

            {/* Merchant ID */}
            <li className="col-12">
              <label className="filedHd">
                {t("merchant_id")}
                {!isEdit && <span className="text-danger"> *</span>}
              </label>
              <input
                type="text"
                name="merchant_id"
                className="formInput"
                value={formData.merchant_id}
                onChange={handleChange}
                placeholder={
                  isEdit ? "Leave blank to keep current" : t("merchant_id")
                }
              />
              {errors?.merchant_id && (
                <span className="text-danger small">{errors.merchant_id}</span>
              )}
            </li>

            {/* Payment Method ID */}
            <li className="col-12">
              <label className="filedHd">
                {t("payment_method_id")}
                {!isEdit && <span className="text-danger"> *</span>}
              </label>
              <input
                type="text"
                name="payment_method_id"
                className="formInput"
                value={formData.payment_method_id}
                onChange={handleChange}
                placeholder={
                  isEdit
                    ? "Leave blank to keep current"
                    : t("payment_method_id")
                }
              />
              {errors?.payment_method_id && (
                <span className="text-danger small">
                  {errors.payment_method_id}
                </span>
              )}
            </li>

            <li className="col-12 text-end mt-2">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <div className="spinner-border loaderStyle2" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </li>
          </ul>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddBankDetails;
